import {
    AppState,
    Block,
    BreachEvent,
    isArticle,
    PublicationType,
    RequiredAccess,
    TogglesReduxState,
    useFeature,
} from '@news-mono/web-common'
import H from 'history'
import React from 'react'
import { connect, DispatchProp, useSelector } from 'react-redux'
import { isLiveSportArticle } from '../../templates/Publication/lib/get-inline-positioned-sports-hub-cta'
import {
    InlinePositionedContent,
    TargetedContent,
} from '../../templates/Publication/Publication.props'
import {
    responsiveComponent,
    ResponsiveProps,
} from '../../__helpers/responsive-component'
import { EntitledToView } from '../entitlements/EntitledToView'
import { useBreachWallConfig } from './components/BreachNotificationWrapper/BreachNotificationWrapper'
import { LazyBreachNotificationWrapper } from './components/BreachNotificationWrapper/BreachNotificationWrapper.lazy'
import { createIsEntitledChangeHandler } from './createIsEntitledChangeHandler'

interface OwnProps {
    requiredAccess: RequiredAccess
    location: H.Location
    blocks: Block[]
    slug: string
    // inlinePositionedContent and targetedContent are for placing ads between content
    inlinePositionedContent?: InlinePositionedContent[]
    targetedContent?: TargetedContent
    onEvent: (event: BreachEvent) => void
    children: (content: {
        blocks: Block[]
        inlinePositionedContent: InlinePositionedContent[] | undefined
        targetedContent: TargetedContent | undefined
    }) => React.ReactElement<any>
    publication: PublicationType
}

export type InternalArticleBreachProps = OwnProps & {
    toggles: TogglesReduxState
} & DispatchProp<any> &
    ResponsiveProps
const mapStateToProps = (state: AppState) => ({ toggles: state.toggles })

export const InternalArticleBreach: React.FC<InternalArticleBreachProps> = (
    props,
) => {
    const {
        blocks,
        inlinePositionedContent,
        targetedContent,
        requiredAccess,
        location,
        onEvent,
        slug,
        publication,
    } = props

    const authState = useSelector((state: AppState) => state.authentication)
    const sportBreachEnabled = useFeature('sport-breach-screen-variant')

    const { creativeName, deal, type } = useBreachWallConfig(location)

    const classification = isArticle(publication)
        ? publication.classification
        : undefined

    const showLiveSportHubTrailer =
        classification &&
        isLiveSportArticle(
            classification,
            publication.primaryTopic,
            publication.secondaryTopics,
        )

    return (
        <EntitledToView
            requiredAccess={requiredAccess}
            articleSlug={slug}
            onIsEntitledChange={createIsEntitledChangeHandler(
                creativeName,
                deal,
                location,
                onEvent,
                authState.isLoggedIn,
                sportBreachEnabled,
            )}
            entitled={() => {
                return props.children({
                    blocks,
                    inlinePositionedContent,
                    targetedContent,
                })
            }}
            notEntitled={() => {
                return (
                    <LazyBreachNotificationWrapper
                        publication={publication}
                        requiredAccess={requiredAccess}
                        breachDeal={deal}
                        breachCreativeName={creativeName}
                        onEvent={props.onEvent}
                        type={type}
                        contentOptions={{ showLiveSportHubTrailer }}
                    />
                )
            }}
        />
    )
}

export const ArticleBreach: React.ComponentType<OwnProps> =
    responsiveComponent<OwnProps>(
        connect<{ toggles: TogglesReduxState }, {}, OwnProps, AppState>(
            mapStateToProps,
            null,
            null,
            {},
        )(InternalArticleBreach),
    )

ArticleBreach.displayName = 'ArticleBreach'
